import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { runPolicyEvaluateAccess } from '../utils/policyAccess';
import { runPolicyEvaluateBatchProbe } from '../utils/policyProbe';
import { usePolicyStore } from '../stores/policyStore';

/**
 * Realtime gates for a group of button-level actions (delete / download / filter …).
 *
 * Upgrades the cache-only `usePolicyStore(s => s.can(action))` pattern to the same
 * realtime enforcement the TMF panes use: the cached value paints immediately, then one
 * batch evaluate confirms it and takes over. Re-checks on window focus and tab revisit,
 * so a permission revoked mid-session disables the button without a page refresh.
 *
 * @param {string[]} actions Action keys to gate.
 * @param {object} [opts]
 * @param {object} [opts.resource] Resource scope (e.g. `{ study_id }`).
 * @param {Record<string, string>} [opts.deniedReasons] Per-action fallback copy.
 * @param {boolean} [opts.enabled=true] Skip probing when false (e.g. nothing selected).
 * @param {string} [opts.probeSignature=''] Re-probe when this changes (e.g. study id).
 * @returns {{ can: (action: string) => boolean, reasonFor: (action: string) => string,
 *   checked: boolean, loading: boolean }}
 */
export function usePolicyActionGates(
  actions,
  { resource, deniedReasons, enabled = true, probeSignature = '' } = {}
) {
  const actionList = useMemo(() => (actions || []).filter(Boolean), [actions]);
  const actionsKey = actionList.join('|');
  const policiesLoaded = usePolicyStore((s) => s.loaded);
  const storeCan = usePolicyStore((s) => s.can);

  const [probed, setProbed] = useState(null);
  const [loading, setLoading] = useState(false);

  // Read via refs so inline object/array literals from callers don't retrigger the effect.
  const resourceRef = useRef(resource);
  const deniedReasonsRef = useRef(deniedReasons);
  resourceRef.current = resource;
  deniedReasonsRef.current = deniedReasons;

  const runProbe = useCallback(async () => {
    if (!enabled || actionsKey === '') return;
    setLoading(true);
    try {
      const results = await runPolicyEvaluateBatchProbe(actionsKey.split('|'), {
        resource: resourceRef.current || {},
        deniedReasons: deniedReasonsRef.current || {},
      });
      setProbed(results);
    } catch {
      // Transport failure: keep the cached answer rather than locking the user out.
    } finally {
      setLoading(false);
    }
  }, [enabled, actionsKey]);

  useEffect(() => {
    if (!enabled || actionsKey === '') {
      setProbed(null);
      return undefined;
    }
    let cancelled = false;
    const run = () => { if (!cancelled) runProbe(); };
    run();
    const onFocus = () => run();
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, actionsKey, probeSignature, runProbe]);

  const can = useCallback(
    (action) => {
      // Probe result wins once available; until then fall back to the cache so the UI
      // paints without waiting (same cache-then-verify order as the TMF panes).
      if (probed && Object.prototype.hasOwnProperty.call(probed, action)) {
        return probed[action].ok;
      }
      return policiesLoaded ? storeCan(action) === true : false;
    },
    [probed, policiesLoaded, storeCan]
  );

  const reasonFor = useCallback(
    (action) => (probed?.[action]?.ok === false ? probed[action].reason : ''),
    [probed]
  );

  return { can, reasonFor, checked: probed !== null, loading };
}

/**
 * Guard an action behind policy evaluation: optional resource id resolution,
 * evaluateAccess via runPolicyEvaluateAccess, deny UI, success callback, destructive toast on API errors.
 */
export function usePolicyGatedAction({ toast }) {
  const [checking, setChecking] = useState(false);
  const runningRef = useRef(false);

  const run = useCallback(
    async ({
      requireResourceId = true,
      resolveResourceId,
      missingResourceReason,
      evaluate,
      activity,
      onPermitted,
      denyReasonFallback = 'You do not have permission to perform this action.',
      onDenied,
      errorToastTitle = 'Unable to verify access',
      errorFallbackMessage = 'Please try again.',
    }) => {
      if (runningRef.current) return;
      runningRef.current = true;
      setChecking(true);
      try {
        let resourceId;
        if (requireResourceId !== false) {
          resourceId =
            typeof resolveResourceId === 'function' ? resolveResourceId() : resolveResourceId;
          if (!resourceId) {
            onDenied?.({ activity, reason: missingResourceReason });
            return;
          }
        }

        const outcome = await runPolicyEvaluateAccess(
          () =>
            requireResourceId === false
              ? evaluate()
              : evaluate(resourceId),
          { fallbackMessage: errorFallbackMessage }
        );

        if (!outcome.ok) {
          toast({
            title: errorToastTitle,
            description: outcome.message,
            variant: 'destructive',
          });
          return;
        }

        const decision = outcome.decision;
        if (decision?.permitted) {
          onPermitted?.();
          return;
        }
        onDenied?.({
          activity,
          reason: decision?.reason || denyReasonFallback,
        });
      } finally {
        runningRef.current = false;
        setChecking(false);
      }
    },
    [toast]
  );

  return { checking, run };
}

/** True while GET /policy/me is in flight (e.g. study switch). */
export function usePolicyReloading() {
  return usePolicyStore((s) => s.loading);
}

/**
 * Zustand (/policy/me) for sidebar visibility + realtime middleware probe on tab open.
 *
 * Flow:
 *   1. Cache deny → access denied (no API)
 *   2. Cache allow → render tab content; child performs ONE GET with *Viewer=true
 *   3. 403 → denied card | 200 → show data
 *
 * Use `skipProbe: true` on dashboard gates when the mounted child already calls the
 * same API with *Viewer=true (avoids duplicate network requests on tab click).
 *
 * Important: `probe` / `parseDenial` are stored in refs so inline arrow functions
 * do not retrigger this effect every render (infinite request loop).
 */
export function usePolicyTabGate({
  active,
  policiesLoaded,
  cachedPermitted,
  probe,
  parseDenial,
  deniedReason = 'You do not have access to this section.',
  skipProbe = false,
  /** Re-run probe when this value changes (e.g. selectedStudy id). */
  probeSignature = '',
}) {
  const [state, setState] = useState({
    loading: false,
    permitted: false,
    reason: '',
    checked: false,
  });

  const probeRef = useRef(probe);
  const parseDenialRef = useRef(parseDenial);
  probeRef.current = probe;
  parseDenialRef.current = parseDenial;

  const inFlightSignatureRef = useRef(null);

  useEffect(() => {
    if (!active) {
      inFlightSignatureRef.current = null;
      setState({ loading: false, permitted: false, reason: '', checked: false });
      return;
    }

    if (skipProbe) {
      setState({ loading: false, permitted: true, reason: '', checked: true });
      return;
    }

    if (!policiesLoaded) {
      setState({ loading: true, permitted: false, reason: '', checked: false });
      return;
    }

    if (!cachedPermitted) {
      inFlightSignatureRef.current = null;
      setState({ loading: false, permitted: false, reason: deniedReason, checked: true });
      return;
    }

    const sig = String(probeSignature ?? '');
    if (inFlightSignatureRef.current === sig) {
      return;
    }
    inFlightSignatureRef.current = sig;

    let cancelled = false;
    setState({ loading: true, permitted: false, reason: '', checked: false });

    (async () => {
      try {
        await probeRef.current();
        if (!cancelled) {
          setState({ loading: false, permitted: true, reason: '', checked: true });
        }
      } catch (error) {
        if (cancelled) return;
        const denial = parseDenialRef.current?.(error);
        setState({
          loading: false,
          permitted: false,
          reason: denial?.reason || deniedReason,
          checked: true,
        });
      }
    })();

    return () => {
      cancelled = true;
      if (inFlightSignatureRef.current === sig) {
        inFlightSignatureRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- probe/parseDenial via refs only
  }, [active, policiesLoaded, cachedPermitted, skipProbe, deniedReason, probeSignature]);

  const cacheDenied = active && policiesLoaded && !cachedPermitted;
  const gatePending =
    active &&
    !skipProbe &&
    policiesLoaded &&
    cachedPermitted &&
    (!state.checked || state.loading);
  const realtimeDenied = active && state.checked && !state.permitted && cachedPermitted;

  return {
    cacheDenied,
    gatePending,
    realtimeDenied,
    permitted: state.permitted && state.checked,
    reason: state.reason,
    showSpinner: cacheDenied ? false : gatePending,
    showDenied: cacheDenied || realtimeDenied,
    deniedMessage: cacheDenied ? deniedReason : state.reason || deniedReason,
  };
}

export default usePolicyTabGate;
